import { useCallback, useEffect, useState } from 'react';
import type { Parser } from './reads.ts';

/**
 * What an invite this browser holds says about itself. Plain fetch, not `send`: the landing page
 * and the gate read it too, signed out or not let in. A failed read is just nothing held.
 */
export function useHeld<B, T>(path: string, schema: Parser<B>, pick: (body: B) => T | null) {
  const [held, setHeld] = useState<T | null>(null);
  const [reads, setReads] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reads` asks again on purpose.
  useEffect(() => {
    const abort = new AbortController();
    fetch(path, { signal: abort.signal, credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: unknown) => {
        const parsed = schema.safeParse(body);
        setHeld(parsed.success ? pick(parsed.data) : null);
      })
      .catch(() => {});
    return () => abort.abort();
  }, [path, schema, pick, reads]);
  const reload = useCallback(() => setReads((count) => count + 1), []);
  return { held, setHeld, reload };
}
